import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { jobsAPI, contractorsAPI, aiAPI } from '../services/api';
import { DatePicker } from '../components/DatePicker';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  Plus, Search, Edit, Trash2, Briefcase, MapPin, Calendar, DollarSign,
  ChevronRight, Clock, CheckCircle, AlertCircle, PauseCircle, XCircle, Activity, Receipt,
  Sparkles, Wand2, Loader2, Archive, RotateCcw, Copy, FileInput
} from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';

const statusConfig = {
  pending: { label: 'Pending', color: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30', icon: Clock },
  in_progress: { label: 'In Progress', color: 'bg-blue-500/20 text-blue-400 border-blue-500/30', icon: Activity },
  completed: { label: 'Completed', color: 'bg-green-500/20 text-green-400 border-green-500/30', icon: CheckCircle },
  on_hold: { label: 'On Hold', color: 'bg-orange-500/20 text-orange-400 border-orange-500/30', icon: PauseCircle },
  cancelled: { label: 'Cancelled', color: 'bg-red-500/20 text-red-400 border-red-500/30', icon: XCircle },
  invoiced: { label: 'Invoiced', color: 'bg-purple-500/20 text-purple-400 border-purple-500/30', icon: Receipt },
};

const statusOptions = ['pending', 'in_progress', 'completed', 'on_hold', 'cancelled', 'invoiced'];

export const JobsPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [jobs, setJobs] = useState([]);
  const [archivedJobs, setArchivedJobs] = useState([]);
  const [contractors, setContractors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [activeTab, setActiveTab] = useState('active');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingJob, setEditingJob] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    client_name: '',
    client_email: '',
    client_phone: '',
    address: '',
    start_date: '',
    end_date: '',
    budget: '',
    is_roof: false,
  });
  const [roofFilter, setRoofFilter] = useState(false);
  
  // AI Job Creation state
  const [aiDialogOpen, setAiDialogOpen] = useState(false);
  const [aiDescription, setAiDescription] = useState('');
  const [aiLoading, setAiLoading] = useState(false);
  const [aiSuggestion, setAiSuggestion] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [jobsRes, archivedRes, contractorsRes] = await Promise.all([
        jobsAPI.getAll(),
        jobsAPI.getArchived(),
        user?.role !== 'trade' ? contractorsAPI.getAll() : Promise.resolve({ data: [] })
      ]);
      setJobs(jobsRes.data);
      setArchivedJobs(archivedRes.data || []);
      setContractors(contractorsRes.data);
    } catch (error) {
      toast.error('Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const data = {
        ...formData,
        budget: formData.budget ? parseFloat(formData.budget) : null,
      };

      if (editingJob) {
        await jobsAPI.update(editingJob.id, data);
        toast.success('Job updated');
      } else {
        await jobsAPI.create(data);
        toast.success('Job created');
      }
      
      fetchData();
      resetForm();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Operation failed');
    }
  };

  const handleEdit = (e, job) => {
    e.stopPropagation();
    setEditingJob(job);
    setFormData({
      title: job.title,
      description: job.description || '',
      client_name: job.client_name,
      client_email: job.client_email || '',
      client_phone: job.client_phone || '',
      address: job.address,
      start_date: job.start_date || '',
      end_date: job.end_date || '',
      budget: job.budget?.toString() || '',
      is_roof: !!job.is_roof,
    });
    setDialogOpen(true);
  };

  const handleStatusChange = async (e, jobId, newStatus) => {
    e.stopPropagation();
    try {
      const res = await jobsAPI.updateStatus(jobId, newStatus);
      if (res.data?.archived) {
        toast.success(`Job marked completed & archived`);
      } else {
        toast.success('Status updated');
      }
      fetchData();
    } catch (error) {
      const detail = error.response?.data?.detail;
      if (error.response?.status === 409 && detail?.code === 'unbilled') {
        const lines = [
          detail.message,
          ``,
          `PO total:        $${(detail.po_total || 0).toFixed(2)}`,
          `Invoiced:        $${(detail.invoiced_to_date || 0).toFixed(2)}`,
          `Unbilled:        $${(detail.unbilled || 0).toFixed(2)}`,
          detail.has_final_invoice ? '' : 'No FINAL invoice issued yet.',
          ``,
          `Complete & archive anyway?`,
        ].filter(Boolean).join('\n');
        if (window.confirm(lines)) {
          try {
            const r2 = await jobsAPI.updateStatus(jobId, newStatus, { acknowledge_unbilled: true });
            toast.success(r2.data?.archived ? 'Job completed & archived' : 'Status updated');
            fetchData();
          } catch (e2) {
            toast.error(e2.response?.data?.detail?.message || 'Failed to update status');
          }
        }
      } else {
        toast.error(typeof detail === 'string' ? detail : (detail?.message || 'Failed to update status'));
      }
    }
  };

  const handleDelete = async (e, id) => {
    e.stopPropagation();
    if (!window.confirm('Are you sure you want to archive this job? You can restore it later.')) return;
    try {
      await jobsAPI.delete(id);
      toast.success('Job archived');
      fetchData();
    } catch (error) {
      toast.error('Failed to archive job');
    }
  };

  const handleDuplicate = async (e, id) => {
    e.stopPropagation();
    try {
      const response = await jobsAPI.duplicate(id);
      toast.success(`Job duplicated: ${response.data.title}`);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to duplicate job');
    }
  };

  const handleRestore = async (e, id) => {
    e.stopPropagation();
    try {
      await jobsAPI.restore(id);
      toast.success('Job restored');
      fetchData();
    } catch (error) {
      toast.error('Failed to restore job');
    }
  };

  const handlePermanentDelete = async (e, id) => {
    e.stopPropagation();
    if (!window.confirm('Are you sure you want to PERMANENTLY delete this job? This cannot be undone.')) return;
    try {
      await jobsAPI.permanentDelete(id);
      toast.success('Job permanently deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete job');
    }
  };

  const resetForm = () => {
    setDialogOpen(false);
    setEditingJob(null);
    setFormData({
      title: '',
      description: '',
      client_name: '',
      client_email: '',
      client_phone: '',
      address: '',
      start_date: '',
      end_date: '',
      budget: '',
      is_roof: false,
    });
  };

  // AI Job Extraction
  const handleAIExtract = async () => {
    if (!aiDescription.trim()) {
      toast.error('Please enter a job description');
      return;
    }
    
    setAiLoading(true);
    try {
      const response = await aiAPI.createJob({ description: aiDescription });
      setAiSuggestion(response.data);
      toast.success('AI extracted job details!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'AI extraction failed');
    } finally {
      setAiLoading(false);
    }
  };

  const applyAISuggestion = () => {
    if (!aiSuggestion) return;
    
    setFormData({
      title: aiSuggestion.title || '',
      description: aiSuggestion.description || '',
      client_name: aiSuggestion.client_name || '',
      client_email: aiSuggestion.client_email || '',
      client_phone: aiSuggestion.client_phone || '',
      address: aiSuggestion.address || '',
      start_date: '',
      end_date: '',
      budget: aiSuggestion.estimated_value?.toString() || '',
    });
    
    // Close AI dialog and open create dialog
    setAiDialogOpen(false);
    setAiSuggestion(null);
    setAiDescription('');
    setDialogOpen(true);
    toast.success('Job details applied! Review and save.');
  };

  const resetAIDialog = () => {
    setAiDialogOpen(false);
    setAiDescription('');
    setAiSuggestion(null);
  };

  // Filter out invoiced jobs - they show in Invoicing page
  const filteredJobs = jobs.filter(job => {
    // Hide invoiced jobs from main jobs list
    if (job.status === 'invoiced') return false;

    const matchesSearch = job.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      job.client_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      job.address.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || job.status === statusFilter;
    const matchesRoof = !roofFilter || !!job.is_roof;
    return matchesSearch && matchesStatus && matchesRoof;
  });

  // Filter archived jobs
  const filteredArchivedJobs = archivedJobs.filter(job => {
    const matchesSearch = job.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      job.client_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      job.address.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesRoof = !roofFilter || !!job.is_roof;
    return matchesSearch && matchesRoof;
  });

  const getContractorNames = (ids) => {
    if (!ids || ids.length === 0) return '';
    return ids.map(id => contractors.find(c => c.id === id)?.name || 'Unknown').join(', ');
  };

  // Job Card Component
  const JobCard = ({ job, isArchived = false }) => {
    const status = statusConfig[job.status] || statusConfig.pending;
    const StatusIcon = status.icon;
    
    return (
      <Card 
        className={`bg-card border-border rounded-sm cursor-pointer hover:border-primary/50 transition-colors ${isArchived ? 'opacity-70' : ''}`}
        onClick={() => !isArchived && navigate(`/jobs/${job.id}`)}
        data-testid={`job-card-${job.id}`}
      >
        <CardContent className="p-4">
          <div className="flex items-start justify-between gap-4">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-2 flex-wrap">
                <h3 className="font-bold truncate">{job.title}</h3>
                {job.has_progress_invoices && (
                  <span
                    className="text-[10px] font-bold uppercase tracking-wider bg-yellow-500/20 text-yellow-400 border border-yellow-500/30 px-2 py-0.5 rounded-sm flex items-center gap-1"
                    title="Has progress invoices — remember to issue a final invoice"
                    data-testid={`job-progress-badge-${job.id}`}
                  >
                    <DollarSign className="h-2.5 w-2.5" /> Progress
                  </span>
                )}
                {job.is_roof && (
                  <span className="text-[10px] font-bold uppercase tracking-wider bg-orange-500/20 text-orange-400 border border-orange-500/30 px-2 py-0.5 rounded-sm" data-testid={`job-roof-badge-${job.id}`}>
                    Roof
                  </span>
                )}
                {isArchived && (
                  <span className="text-xs bg-muted px-2 py-0.5 rounded">Archived</span>
                )}
              </div>
              <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm text-muted-foreground">
                <span className="flex items-center gap-1">
                  <Briefcase className="h-3 w-3" />
                  {job.client_name}
                </span>
                <span className="flex items-center gap-1">
                  <MapPin className="h-3 w-3" />
                  {job.address}
                </span>
                {job.start_date && (
                  <span className="flex items-center gap-1">
                    <Calendar className="h-3 w-3" />
                    {format(new Date(job.start_date), 'MMM d, yyyy')}
                  </span>
                )}
                {job.budget && (
                  <span className="flex items-center gap-1 font-mono">
                    <DollarSign className="h-3 w-3" />
                    {job.budget.toLocaleString()}
                  </span>
                )}
              </div>
            </div>
            <div className="flex items-center gap-2">
              {!isArchived ? (
                <>
                  <Select value={job.status} onValueChange={(v) => handleStatusChange({ stopPropagation: () => {} }, job.id, v)}>
                    <SelectTrigger 
                      className={`w-auto h-auto px-2 py-1 border ${status.color} rounded-sm text-xs font-mono uppercase`}
                      onClick={(e) => e.stopPropagation()}
                    >
                      <StatusIcon className="h-3 w-3 mr-1" />
                      <span className="hidden sm:inline">{status.label}</span>
                    </SelectTrigger>
                    <SelectContent className="bg-card border-border">
                      {statusOptions.map(s => (
                        <SelectItem key={s} value={s}>{statusConfig[s].label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {user?.role !== 'trade' && (
                    <>
                      <Button variant="ghost" size="icon" onClick={(e) => handleEdit(e, job)} data-testid={`edit-job-btn-${job.id}`}>
                        <Edit className="h-4 w-4" />
                      </Button>
                      <Button variant="ghost" size="icon" onClick={(e) => handleDuplicate(e, job.id)} title="Duplicate Job" data-testid={`duplicate-job-btn-${job.id}`}>
                        <Copy className="h-4 w-4 text-muted-foreground" />
                      </Button>
                      <Button variant="ghost" size="icon" onClick={(e) => handleDelete(e, job.id)} title="Archive" data-testid={`archive-job-btn-${job.id}`}>
                        <Archive className="h-4 w-4 text-muted-foreground" />
                      </Button>
                    </>
                  )}
                </>
              ) : (
                <>
                  <Button variant="ghost" size="sm" onClick={(e) => handleRestore(e, job.id)} title="Restore" className="text-green-500">
                    <RotateCcw className="h-4 w-4 mr-1" />
                    Restore
                  </Button>
                  {(user?.role === 'admin' || user?.role === 'superuser') && (
                    <Button variant="ghost" size="icon" onClick={(e) => handlePermanentDelete(e, job.id)} title="Delete Permanently" className="text-destructive">
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  )}
                </>
              )}
              {!isArchived && <ChevronRight className="h-4 w-4 text-muted-foreground" />}
            </div>
          </div>
        </CardContent>
      </Card>
    );
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="jobs-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold">Jobs</h1>
          <p className="text-muted-foreground mt-1">Manage your construction projects</p>
        </div>
        {user?.role !== 'trade' && (
          <div className="flex gap-2">
            {/* Import from supplied PO */}
            <Button
              variant="outline"
              className="rounded-sm border-primary/50 hover:border-primary"
              onClick={() => navigate('/jobs/import-from-po')}
              data-testid="import-from-po-btn"
            >
              <FileInput className="h-4 w-4 mr-2 text-primary" />
              Import from PO
            </Button>
            {/* AI Job Creation Dialog */}
            <Dialog open={aiDialogOpen} onOpenChange={(open) => { if (!open) resetAIDialog(); else setAiDialogOpen(true); }}>
              <DialogTrigger asChild>
                <Button variant="outline" className="rounded-sm border-primary/50 hover:border-primary" data-testid="ai-create-job-btn">
                  <Sparkles className="h-4 w-4 mr-2 text-primary" />
                  AI Create
                </Button>
              </DialogTrigger>
              <DialogContent className="bg-card border-border max-w-lg">
                <DialogHeader>
                  <DialogTitle className="flex items-center gap-2">
                    <Wand2 className="h-5 w-5 text-primary" />
                    AI Job Creation
                  </DialogTitle>
                </DialogHeader>
                <div className="space-y-4">
                  <p className="text-sm text-muted-foreground">
                    Paste any text description (email, notes, quote request) and AI will extract job details automatically.
                  </p>
                  <Textarea
                    value={aiDescription}
                    onChange={(e) => setAiDescription(e.target.value)}
                    placeholder="e.g., John Smith needs a bathroom renovation at 45 George St Brisbane. He wants new tiles, vanity, and shower. Budget around $15k..."
                    className="bg-input border-border rounded-sm min-h-[120px]"
                    data-testid="ai-description-input"
                  />
                  
                  {aiSuggestion && (
                    <Card className="bg-primary/5 border-primary/20">
                      <CardHeader className="pb-2">
                        <CardTitle className="text-sm flex items-center gap-2">
                          <CheckCircle className="h-4 w-4 text-green-500" />
                          Extracted Details
                        </CardTitle>
                      </CardHeader>
                      <CardContent className="space-y-2 text-sm">
                        <div><span className="text-muted-foreground">Title:</span> {aiSuggestion.title}</div>
                        <div><span className="text-muted-foreground">Client:</span> {aiSuggestion.client_name || 'Not specified'}</div>
                        <div><span className="text-muted-foreground">Address:</span> {aiSuggestion.address || 'Not specified'}</div>
                        <div><span className="text-muted-foreground">Est. Value:</span> ${aiSuggestion.estimated_value?.toLocaleString() || '0'}</div>
                        <div><span className="text-muted-foreground">Type:</span> {aiSuggestion.job_type}</div>
                        {aiSuggestion.suggested_trades?.length > 0 && (
                          <div><span className="text-muted-foreground">Trades Needed:</span> {aiSuggestion.suggested_trades.join(', ')}</div>
                        )}
                      </CardContent>
                    </Card>
                  )}
                  
                  <div className="flex justify-end gap-2">
                    <Button type="button" variant="outline" onClick={resetAIDialog} className="rounded-sm">
                      Cancel
                    </Button>
                    {!aiSuggestion ? (
                      <Button 
                        onClick={handleAIExtract} 
                        disabled={aiLoading || !aiDescription.trim()}
                        className="rounded-sm"
                        data-testid="ai-extract-btn"
                      >
                        {aiLoading ? (
                          <>
                            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                            Extracting...
                          </>
                        ) : (
                          <>
                            <Wand2 className="h-4 w-4 mr-2" />
                            Extract Details
                          </>
                        )}
                      </Button>
                    ) : (
                      <Button onClick={applyAISuggestion} className="rounded-sm bg-green-600 hover:bg-green-700" data-testid="ai-apply-btn">
                        <CheckCircle className="h-4 w-4 mr-2" />
                        Use These Details
                      </Button>
                    )}
                  </div>
                </div>
              </DialogContent>
            </Dialog>
            
            {/* Regular New Job Dialog */}
            <Dialog open={dialogOpen} onOpenChange={(open) => { if (!open) resetForm(); else setDialogOpen(true); }}>
              <DialogTrigger asChild>
                <Button className="uppercase font-bold tracking-wide rounded-sm" data-testid="add-job-btn">
                  <Plus className="h-4 w-4 mr-2" />
                  New Job
                </Button>
              </DialogTrigger>
            <DialogContent className="bg-card border-border max-w-lg max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>{editingJob ? 'Edit Job' : 'Create New Job'}</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label>Job Title *</Label>
                  <Input
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    required
                    placeholder="e.g., Kitchen Renovation - 123 Main St"
                    className="bg-input border-border rounded-sm"
                    data-testid="job-title"
                  />
                </div>

                <div className="space-y-2">
                  <Label>Description</Label>
                  <Textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    placeholder="Detailed job description..."
                    className="bg-input border-border rounded-sm"
                    rows={3}
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Client Name *</Label>
                    <Input
                      value={formData.client_name}
                      onChange={(e) => setFormData({ ...formData, client_name: e.target.value })}
                      required
                      className="bg-input border-border rounded-sm"
                      data-testid="job-client-name"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Client Phone</Label>
                    <Input
                      value={formData.client_phone}
                      onChange={(e) => setFormData({ ...formData, client_phone: e.target.value })}
                      className="bg-input border-border rounded-sm"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>Client Email</Label>
                  <Input
                    type="email"
                    value={formData.client_email}
                    onChange={(e) => setFormData({ ...formData, client_email: e.target.value })}
                    className="bg-input border-border rounded-sm"
                  />
                </div>

                <div className="space-y-2">
                  <Label>Site Address *</Label>
                  <Input
                    value={formData.address}
                    onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                    required
                    className="bg-input border-border rounded-sm"
                    data-testid="job-address"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Start Date</Label>
                    <DatePicker
                      value={formData.start_date}
                      onChange={(d) => setFormData({ ...formData, start_date: d })}
                      placeholder="Start date"
                      data-testid="job-start-date"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>End Date</Label>
                    <DatePicker
                      value={formData.end_date}
                      onChange={(d) => setFormData({ ...formData, end_date: d })}
                      placeholder="End date"
                      data-testid="job-end-date"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>Budget ($)</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={formData.budget}
                    onChange={(e) => setFormData({ ...formData, budget: e.target.value })}
                    className="bg-input border-border rounded-sm"
                  />
                </div>

                <label className="flex items-center gap-2 cursor-pointer select-none pt-2" data-testid="job-form-is-roof">
                  <input
                    type="checkbox"
                    checked={!!formData.is_roof}
                    onChange={(e) => setFormData({ ...formData, is_roof: e.target.checked })}
                    className="h-4 w-4 accent-orange-500"
                  />
                  <span className="text-sm">This is a roof job (adds a Roof badge in the job list)</span>
                </label>

                <div className="flex justify-end gap-2">
                  <Button type="button" variant="outline" onClick={resetForm} className="rounded-sm">Cancel</Button>
                  <Button type="submit" className="uppercase font-bold tracking-wide rounded-sm" data-testid="job-submit">
                    {editingJob ? 'Update' : 'Create'}
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
          </div>
        )}
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search jobs..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10 bg-input border-border rounded-sm"
            data-testid="job-search"
          />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-[180px] bg-input border-border rounded-sm">
            <SelectValue placeholder="Filter by status" />
          </SelectTrigger>
          <SelectContent className="bg-card border-border">
            <SelectItem value="all">All Status</SelectItem>
            <SelectItem value="pending">Pending</SelectItem>
            <SelectItem value="in_progress">In Progress</SelectItem>
            <SelectItem value="completed">Completed</SelectItem>
            <SelectItem value="on_hold">On Hold</SelectItem>
            <SelectItem value="cancelled">Cancelled</SelectItem>
          </SelectContent>
        </Select>
        <label className="flex items-center gap-2 px-3 h-10 rounded-sm border border-border bg-input cursor-pointer select-none" data-testid="roof-filter-toggle">
          <input
            type="checkbox"
            checked={roofFilter}
            onChange={(e) => setRoofFilter(e.target.checked)}
            className="h-4 w-4 accent-orange-500"
          />
          <span className="text-sm">Roof jobs only</span>
        </label>
      </div>

      {/* Jobs List with Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="bg-muted rounded-sm mb-4">
          <TabsTrigger value="active" className="rounded-sm data-[state=active]:bg-background">
            Active Jobs ({filteredJobs.length})
          </TabsTrigger>
          <TabsTrigger value="archived" className="rounded-sm data-[state=active]:bg-background">
            <Archive className="h-4 w-4 mr-1" />
            Archived ({filteredArchivedJobs.length})
          </TabsTrigger>
        </TabsList>
        
        <TabsContent value="active">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            </div>
          ) : filteredJobs.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-64 text-muted-foreground">
              <Briefcase className="h-12 w-12 mb-4" />
              <p>No jobs found</p>
            </div>
          ) : (
            <div className="space-y-2">
              {filteredJobs.map((job) => (
                <JobCard key={job.id} job={job} />
              ))}
            </div>
          )}
        </TabsContent>
        
        <TabsContent value="archived">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            </div>
          ) : filteredArchivedJobs.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-64 text-muted-foreground">
              <Archive className="h-12 w-12 mb-4" />
              <p>No archived jobs</p>
              <p className="text-sm">Jobs you archive will appear here</p>
            </div>
          ) : (
            <div className="space-y-2">
              {filteredArchivedJobs.map((job) => (
                <JobCard key={job.id} job={job} isArchived />
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default JobsPage;
